/**
 * Schéma d'un générateur de devis — version 1.
 *
 * Stocké tel quel dans `public.generators.config` (JSONB).
 * Le champ `version` permet d'évoluer sans casser les générateurs existants :
 * on écrira des migrations up() version N → N+1 côté client.
 *
 * Conventions :
 * - Tous les IDs (steps, fields, options) sont des strings stables (uuid ou slug).
 * - Les IDs de fields sont uniques dans tout le générateur (utilisés par les
 *   règles de visibilité et de pricing).
 */

export type Locale = 'fr' | 'en';
export type Currency = 'EUR' | 'USD' | 'CAD';

export type FieldType =
    | 'select'
    | 'chips'
    | 'slider'
    | 'number'
    | 'radio'
    | 'textarea'
    | 'checkbox-group';

/** Valeur primitive qu'un field peut contenir (réponse d'un utilisateur) */
export type FieldValue = string | number | string[] | boolean | null;

/** Condition logique simple sur la valeur d'un autre field */
export interface VisibilityRule {
    /** ID du field observé */
    fieldId: string;
    /** L'une des conditions ci-dessous (mutuellement exclusives pour le MVP) */
    equals?: string | number;
    notEquals?: string | number;
    in?: Array<string | number>;
    notIn?: Array<string | number>;
    /** Pour les chips multi-select : au moins une des valeurs doit être cochée */
    includes?: string;
}

/** Palier de prix pour un field numérique (slider/number) */
export interface PriceRange {
    /** Borne inférieure incluse */
    min: number;
    /** Borne supérieure exclue ; `null` = pas de limite haute */
    max: number | null;
    /** Montant ajouté si la valeur tombe dans [min, max) */
    amount: number;
}

/** Impact d'un field sur le prix final.
 *  Les types disponibles dépendent du type de field — la restriction
 *  se fait dans l'UI de l'éditeur, pas dans ce type.
 */
export type PricingImpact =
    | { type: 'none' }
    /** Ajoute un montant fixe (utile surtout pour les règles globales) */
    | { type: 'add'; amount: number }
    /** Multiplie le prix courant (utile surtout pour les règles globales) */
    | { type: 'multiply'; factor: number }
    /** Sur slider/number : `valeur × unitPrice` (ex : 45 €/m²) */
    | { type: 'addPerUnit'; unitPrice: number }
    /** Sur les fields à options : un montant par option choisie.
     *  Pour les multi-sélections, les montants s'additionnent. */
    | { type: 'lookup'; amounts: Record<string, number> }
    /** Sur slider/number : montant selon la plage de valeur (paliers). */
    | { type: 'ranges'; ranges: PriceRange[] };

/* ---------- Options pour les fields à choix ---------- */
export interface FieldOption {
    id: string;
    label: string;
    /** Emoji ou icône optionnelle pour les chips */
    icon?: string;
    /** Sous-titre court */
    helper?: string;
}

/* ---------- Types de champs ---------- */
interface BaseField {
    id: string;
    type: FieldType;
    label: string;
    helper?: string;
    required?: boolean;
    visibility?: VisibilityRule;
    pricing?: PricingImpact;
}

export interface SelectField extends BaseField {
    type: 'select';
    options: FieldOption[];
    defaultValue?: string;
}

export interface ChipsField extends BaseField {
    type: 'chips';
    multi: boolean;
    options: FieldOption[];
    defaultValue?: string | string[];
}

export interface SliderField extends BaseField {
    type: 'slider';
    min: number;
    max: number;
    step?: number;
    unit?: string;
    defaultValue?: number;
}

export interface NumberField extends BaseField {
    type: 'number';
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    defaultValue?: number;
}

export interface RadioField extends BaseField {
    type: 'radio';
    options: FieldOption[];
    defaultValue?: string;
}

export interface TextareaField extends BaseField {
    type: 'textarea';
    placeholder?: string;
    maxLength?: number;
    defaultValue?: string;
}

export interface CheckboxGroupField extends BaseField {
    type: 'checkbox-group';
    options: FieldOption[];
    defaultValue?: string[];
}

export type Field =
    | SelectField
    | ChipsField
    | SliderField
    | NumberField
    | RadioField
    | TextareaField
    | CheckboxGroupField;

/* ---------- Étape du wizard ---------- */
export interface Step {
    id: string;
    title: string;
    description?: string;
    fields: Field[];
    visibility?: VisibilityRule;
}

/* ---------- Branding du générateur ---------- */
export interface Branding {
    companyName: string;
    primaryColor: string;
    logoUrl: string | null;
    locale: Locale;
    currency: Currency;
}

/* ---------- Règle de pricing globale ---------- */
export interface PricingRule {
    id: string;
    label: string;
    condition?: VisibilityRule;
    effect: PricingImpact;
}

/* ---------- Pricing global ---------- */
export interface Pricing {
    baseAmount: number;
    rules: PricingRule[];
    /** Message d'explication affiché sous le prix dans le résumé */
    footnote?: string;
}

/* ---------- Captation de leads (formulaire contact après résumé) ---------- */
export interface LeadFieldConfig {
    enabled: boolean;
    required: boolean;
}

export interface LeadCapture {
    /** Si false, pas de capture → bouton "Recommencer" seul après le résumé. */
    enabled: boolean;
    /** Titre affiché au-dessus du formulaire contact */
    title: string;
    /** Description courte sous le titre */
    description: string;
    /** Libellé du bouton de soumission (ex: "Recevoir mon devis") */
    submitLabel: string;
    /** Champs standards du formulaire contact */
    fields: {
        name: LeadFieldConfig;
        email: LeadFieldConfig;
        phone: LeadFieldConfig;
        company: LeadFieldConfig;
        message: LeadFieldConfig;
    };
    /** Texte de consentement (RGPD) affiché au-dessus du bouton. Optionnel. */
    consentText?: string;
    /** Titre de l'écran de confirmation après envoi */
    successTitle: string;
    /** Message de confirmation */
    successMessage: string;
}

/* ---------- Config complète ---------- */
export interface GeneratorConfigV1 {
    version: 1;
    branding: Branding;
    steps: Step[];
    pricing: Pricing;
    /** Paramètres du formulaire de captation de leads (post-résumé) */
    leadCapture?: LeadCapture;
    /** Métadonnées libres (ex: timestamp de dernière édition, auteur, etc.) */
    meta?: Record<string, unknown>;
}

/** Type "courant" — utilisé partout sauf dans les migrations */
export type GeneratorConfig = GeneratorConfigV1;

/* ---------- Type guards utiles ---------- */
export function isGeneratorConfigV1(x: unknown): x is GeneratorConfigV1 {
    return !!x && typeof x === 'object' && (x as { version?: number }).version === 1;
}
